import { UnifiedAdditionalRecordClassificationType } from '@/feature/patients/types';
import {
  AllergiesTemplate,
  FamilyHistoriesTemplate,
  ProvidersTemplate,
  ReferenceDocumentsTemplate,
  SocialHistoriesTemplate,
  VaccinationsTemplate,
  type AllergyEntry,
  type FamilyHistoriesTemplateRow,
  type ReferenceDocumentEntry,
  type SocialHistoryEntry,
  type VaccinationsTemplateRow,
} from '../../templates';
import { DocumentSource, DocumentType } from '../types';
import type { DocumentEntry, DocumentKey } from '../types';
import type { RawPractitioner } from './_shared';
import { buildSingleItemPdfEntry } from './pdfOverride';

export type AdditionalRecordsKey = Extract<
  DocumentKey,
  { source: DocumentSource.MEDICAL_RECORDS; type: DocumentType.ADDITIONAL }
> & { highlightRecordId?: string };

interface RawAllergy {
  id?: string;
  name?: string;
  recordedDate?: string;
  onsetStartDate?: string;
  onsetEndDate?: string;
  reactionManifestation?: string;
  practitioner?: RawPractitioner;
  dateUploaded?: string;
}

interface RawFamilyHistory {
  id?: string;
  conditionDisplay?: string;
  relationshipDisplay?: string;
  sexDisplay?: string;
  dateUploaded?: string;
}

interface RawSocialHistory {
  id?: string;
  observationCategory?: string;
  observationName?: string;
  observationTimestamp?: string;
  socialHistoryText?: string;
  observationValue?: string;
  dateUploaded?: string;
}

interface RawVaccination {
  id?: string;
  name?: string;
  route?: string;
  status?: string;
  dosageUnit?: string;
  dosageValue?: string;
  occurrenceTime?: string;
  site?: string;
  dateUploaded?: string;
}

interface RawReferenceDocument {
  id?: string;
  documentReferenceId?: string;
  encounterId?: string;
  contentType?: string;
  referenceType?: string;
  typeCoding?: string;
  typeCodingSystem?: string;
  documentMetadata?: { signedUrl?: string };
  dateUploaded?: string;
}

interface RawProvider {
  id?: string;
  name?: string;
  specialty?: string;
  telephones?: string[];
  city?: string;
  state?: string;
  dateUploaded?: string;
  firstName?: string;
  lastName?: string;
  npi?: string;
  providerId?: string;
  street1?: string;
  street2?: string;
  postalCode?: string;
  phoneNumber?: string;
  faxNumber?: string;
  email?: string;
}

interface RawSource {
  id?: string;
  contentType: string;
  signedUrl?: string;
  createdAt: string | Date;
  curatedUnifiedAdditionalAllergy?: RawAllergy;
  curatedUnifiedAdditionalFamilyHistory?: RawFamilyHistory;
  curatedUnifiedAdditionalSocialHistory?: RawSocialHistory;
  curatedUnifiedAdditionalVaccination?: RawVaccination;
  curatedUnifiedAdditionalReferenceDocument?: RawReferenceDocument;
  curatedUnifiedAdditionalProvider?: RawProvider;
}

interface RawDetail {
  id: string;
  name: string;
  category?: string;
  date?: string;
  sourceType: string;
  createdAt: string | Date;
  sources: RawSource[];
}

export interface AdditionalRecordsQueryData {
  details: RawDetail[];
}

const flatSources = (details: RawDetail[]): RawSource[] =>
  details.flatMap((d) => d.sources);

const getUploadedAt = (
  details: RawDetail[],
  getSourceUploadedAt?: (source: RawSource) => string | Date | undefined,
) => {
  if (getSourceUploadedAt) {
    const sourceUploadedAt = flatSources(details)
      .map(getSourceUploadedAt)
      .find(Boolean);
    if (sourceUploadedAt) return sourceUploadedAt;
  }

  return details[0]?.createdAt;
};

const renderAllergies = (
  details: RawDetail[],
  key: AdditionalRecordsKey,
): DocumentEntry => {
  const entries: AllergyEntry[] = flatSources(details)
    .map((s) => s.curatedUnifiedAdditionalAllergy)
    .filter((a): a is RawAllergy => !!a)
    .map((a) => ({
      id: a.id,
      name: a.name,
      reaction: a.reactionManifestation,
      recordedDate: a.recordedDate,
      onsetStart: a.onsetStartDate,
      onsetEnd: a.onsetEndDate,
      practitioner: a.practitioner,
    }));

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.highlightRecordId
      ? `allergy-${key.highlightRecordId}`
      : undefined,
    uploadedAt: getUploadedAt(
      details,
      (source) => source.curatedUnifiedAdditionalAllergy?.dateUploaded,
    ),
    render: (ctx) => (
      <AllergiesTemplate
        entries={entries}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightEntryId={key.highlightRecordId}
      />
    ),
  };
};

const renderFamilyHistory = (
  details: RawDetail[],
  key: AdditionalRecordsKey,
): DocumentEntry => {
  const rows: FamilyHistoriesTemplateRow[] = flatSources(details)
    .map((s) => s.curatedUnifiedAdditionalFamilyHistory)
    .filter((f): f is RawFamilyHistory => !!f)
    .map((f) => ({
      id: f.id,
      relationship: f.relationshipDisplay,
      condition: f.conditionDisplay,
      sex: f.sexDisplay,
    }));

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.highlightRecordId
      ? `family-history-${key.highlightRecordId}`
      : undefined,
    uploadedAt: getUploadedAt(
      details,
      (source) => source.curatedUnifiedAdditionalFamilyHistory?.dateUploaded,
    ),
    render: (ctx) => (
      <FamilyHistoriesTemplate
        rows={rows}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightRowId={key.highlightRecordId}
      />
    ),
  };
};

const renderSocialHistory = (
  details: RawDetail[],
  key: AdditionalRecordsKey,
): DocumentEntry => {
  const entries: SocialHistoryEntry[] = flatSources(details)
    .map((s) => s.curatedUnifiedAdditionalSocialHistory)
    .filter((s): s is RawSocialHistory => !!s)
    .map((s) => ({
      id: s.id,
      name: s.observationName,
      value: s.observationValue,
      notes: s.socialHistoryText,
      observationDate: s.observationTimestamp,
      observationsRecorded: s.observationCategory,
    }));

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.highlightRecordId
      ? `social-history-${key.highlightRecordId}`
      : undefined,
    uploadedAt: getUploadedAt(
      details,
      (source) => source.curatedUnifiedAdditionalSocialHistory?.dateUploaded,
    ),
    render: (ctx) => (
      <SocialHistoriesTemplate
        entries={entries}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightEntryId={key.highlightRecordId}
      />
    ),
  };
};

const renderVaccinations = (
  details: RawDetail[],
  key: AdditionalRecordsKey,
): DocumentEntry => {
  const rows: VaccinationsTemplateRow[] = flatSources(details)
    .map((s) => s.curatedUnifiedAdditionalVaccination)
    .filter((v): v is RawVaccination => !!v)
    .map((v) => ({
      id: v.id,
      vaccine: v.name,
      route: v.route,
      status: v.status,
      dose:
        [v.dosageValue, v.dosageUnit].filter(Boolean).join(' ') || undefined,
      occurrenceTime: v.occurrenceTime,
      site: v.site,
    }));

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.highlightRecordId
      ? `vaccination-${key.highlightRecordId}`
      : undefined,
    uploadedAt: getUploadedAt(
      details,
      (source) => source.curatedUnifiedAdditionalVaccination?.dateUploaded,
    ),
    render: (ctx) => (
      <VaccinationsTemplate
        rows={rows}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightRowId={key.highlightRecordId}
      />
    ),
  };
};

const renderReferenceDocuments = (
  details: RawDetail[],
  key: AdditionalRecordsKey,
): DocumentEntry => {
  const entries: ReferenceDocumentEntry[] = flatSources(details)
    .map((s) => s.curatedUnifiedAdditionalReferenceDocument)
    .filter((r): r is RawReferenceDocument => !!r)
    .map((r) => ({
      id: r.id,
      documentId: r.documentReferenceId,
      reference: r.referenceType,
      encounter: r.encounterId,
      contentType: r.contentType,
      typeCoding: r.typeCoding,
      typeCodingSystem: r.typeCodingSystem,
      signedUrl: r.documentMetadata?.signedUrl,
      sections: [],
    }));

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.highlightRecordId
      ? `reference-document-${key.highlightRecordId}`
      : undefined,
    uploadedAt: getUploadedAt(
      details,
      (source) =>
        source.curatedUnifiedAdditionalReferenceDocument?.dateUploaded,
    ),
    render: (ctx) => (
      <ReferenceDocumentsTemplate
        entries={entries}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightEntryId={key.highlightRecordId}
      />
    ),
  };
};

const renderProviders = (
  details: RawDetail[],
  key: AdditionalRecordsKey,
): DocumentEntry => {
  const rows = flatSources(details)
    .map((s) => s.curatedUnifiedAdditionalProvider)
    .filter((p): p is RawProvider => !!p)
    .map((p) => {
      const name =
        [p.firstName, p.lastName].filter(Boolean).join(' ') || p.name;
      const street = [p.street1, p.street2].filter(Boolean).join(' ');
      const cityState = [p.city, p.state].filter(Boolean).join(', ');
      const address =
        [street, cityState, p.postalCode].filter(Boolean).join(' • ') ||
        undefined;
      return {
        id: p.id,
        name,
        npi: p.npi,
        providerId: p.providerId,
        specialty: p.specialty,
        address,
        phoneNumber: p.phoneNumber,
        faxNumber: p.faxNumber,
        email: p.email,
        telephones: p.telephones,
      };
    });

  return {
    kind: 'template',
    title: key.name,
    highlightAnchorId: key.highlightRecordId
      ? `provider-${key.highlightRecordId}`
      : undefined,
    uploadedAt: getUploadedAt(
      details,
      (source) => source.curatedUnifiedAdditionalProvider?.dateUploaded,
    ),
    render: (ctx) => (
      <ProvidersTemplate
        rows={rows}
        patient={ctx?.patient}
        filename={ctx?.filename}
        generatedAt={new Date()}
        highlightRowId={key.highlightRecordId}
      />
    ),
  };
};

export const adaptAdditionalRecords = (
  data: AdditionalRecordsQueryData,
  key: AdditionalRecordsKey,
): DocumentEntry | undefined => {
  const details = data?.details ?? [];
  if (details.length === 0) return undefined;

  const firstPdfSource = flatSources(details).find(
    (s) => !!s.signedUrl && (s.contentType ?? '').includes('pdf'),
  );
  if (firstPdfSource?.signedUrl) {
    return {
      kind: 'pdf',
      signedUrl: firstPdfSource.signedUrl,
      title: key.name,
      uploadedAt: firstPdfSource.createdAt,
    };
  }

  const sources = flatSources(details);
  const singleSourcePdfEntry = buildSingleItemPdfEntry(
    sources,
    (source) => [
      {
        signedUrl: source.signedUrl,
        contentType: source.contentType,
        title: key.name,
        uploadedAt: source.createdAt,
      },
      {
        signedUrl:
          source.curatedUnifiedAdditionalReferenceDocument?.documentMetadata
            ?.signedUrl,
        contentType:
          source.curatedUnifiedAdditionalReferenceDocument?.contentType,
        title: key.name,
        uploadedAt:
          source.curatedUnifiedAdditionalReferenceDocument?.dateUploaded ??
          source.createdAt,
      },
    ],
    { title: key.name },
  );
  if (singleSourcePdfEntry) return singleSourcePdfEntry;

  switch (key.classificationType) {
    case UnifiedAdditionalRecordClassificationType.ALLERGIES:
      return renderAllergies(details, key);
    case UnifiedAdditionalRecordClassificationType.FAMILY_HISTORY:
      return renderFamilyHistory(details, key);
    case UnifiedAdditionalRecordClassificationType.SOCIAL_HISTORY:
      return renderSocialHistory(details, key);
    case UnifiedAdditionalRecordClassificationType.VACCINATIONS:
      return renderVaccinations(details, key);
    case UnifiedAdditionalRecordClassificationType.ADDITIONAL_REFERENCE_DOCUMENTS:
      return renderReferenceDocuments(details, key);
    case UnifiedAdditionalRecordClassificationType.PROVIDERS:
      return renderProviders(details, key);
    default:
      return undefined;
  }
};
